import type { Organization, User } from '@/types/User'
import { Language } from '@/enums/Language'
import { PendoTrackEvent } from '@/enums/PendoTrackEvent'
import type { Pendo, PendoMetadata, PendoUser } from '@/types/Pendo'

declare let window: Window & {
  pendo: Pendo
}

export default () => {
  const env = import.meta.env.VITE_ENV

  const buildPendoVisitorId = (user: User) => `${env}-${user?.gaHash}`
  const buildPendoAccountId = (schoolOrganization: Organization) => schoolOrganization?.number ? `${env}-${schoolOrganization?.number}` : ''

  const hasPendoSession = (user: User) => window?.pendo?.getVisitorId() === buildPendoVisitorId(user)

  const buildPendoUser = (user: User): PendoUser => {
    const schoolOrganization = user?.schoolOrganizations?.[0]

    return {
      visitor: {
        id: buildPendoVisitorId(user),
        role: user.role.reduce((prev: string, current: string) => prev + current, ''),
        organizationName: schoolOrganization?.name || '',
        organizationNumber: schoolOrganization?.number || '',
        loggedIn: true,
        locale: user?.preferredLanguage ?? Language.bokmal,
        grades: user?.grades.map(grade => grade.code) ?? [],
        subjects: user?.subjects.map(subject => subject.code) ?? [],
      },
      account: {
        id: buildPendoAccountId(schoolOrganization),
        organizationName: schoolOrganization?.name || '',
        organizationNumber: schoolOrganization?.number || '',
      },
    }
  }

  const initPendo = (user: User) => {
    if (env === 'stage') return // disable for stage in order to run e2e tests

    if (hasPendoSession(user)) return

    console.debug('Initializing Pendo session')
    window.pendo.initialize(buildPendoUser(user))
  }

  const showPendoGuide = (guideId: string, reason?: string) => {
    if (!window.pendo) return

    window.pendo.showGuideById(guideId, reason)
  }

  const updatePendo = (user: User): void => {
    if (!window.pendo) return

    console.debug('Updating Pendo session')
    window.pendo.updateOptions(buildPendoUser(user))
  }

  const trackPendoEvent = (trackEvent: PendoTrackEvent, metadata: PendoMetadata): void => {
    if (!window.pendo) return

    window.pendo.track(trackEvent, { ...metadata })
  }

  return {
    initPendo,
    updatePendo,
    trackPendoEvent,
    showPendoGuide,
  }
}
